import { ParsedUrlQuery } from 'querystring'
import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import SearchResultsPage from '@scentregroup/shared/pages/search-results'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import {
  QueryContext,
  SegmentHits,
} from '@scentregroup/shared/components/search/types'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { getAlgoliaApiKeys, NATIONAL_CONFIG } from '@scentregroup/shared/config'
import {
  makeClients,
  makeOffsetlessTimeGetters,
  doQueryAlgolia,
} from '@scentregroup/shared/components/search/algolia-helpers'
import Layout from '@scentregroup/shared/hub-components/layout'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { removeUndefinedProps } from '@scentregroup/shared/helpers/utils'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../types/next'
import { makeApolloClient } from '../../lib/apolloClient'
import { OpenGraph } from '../../lib/open-graph'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { getLocalUserFromRequestCookies } from '@scentregroup/shared/hooks/use-local-user'

interface SearchPageParams extends ParsedUrlQuery {
  centre: string
}

interface SearchPageProps {
  topStripProps: ITopStripProps | null
  algoliaResults?: SegmentHits
  centre: CentreDetails
  country: ReturnType<typeof localeToCountry>
  headerStaticData: CentreStaticData
  canonical: string
}

export async function getServerSideProps(
  context: GetServerSidePropsContext<SearchPageParams>
): Promise<GetServerSidePropsResult<SearchPageProps>> {
  const { locale, params, query, res, req } = context

  const META_INFORMATION =
    locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au
  const canonical = `${META_INFORMATION.url}${req.url}`

  setCacheHeaders(res)

  const country = localeToCountry(locale)
  const apollo = makeApolloClient()

  const centreSlug = params?.centre
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }

  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })

  if (!centre) {
    return {
      notFound: true,
    }
  }

  const headerStaticData = await fetchCentreStaticData(apollo, {
    country,
    centre,
  })
  const algoliaClients = makeClients(getAlgoliaApiKeys())
  const searchQuery = Array.isArray(query.q) ? query.q[0] : query.q
  const timeGetters = makeOffsetlessTimeGetters(country, centre)
  const user = getLocalUserFromRequestCookies(req.cookies)
  const searchContext: QueryContext = {
    country,
    centre,
    user,
    ...timeGetters,
  }

  const [algoliaResults, topStripProps] = await Promise.all([
    doQueryAlgolia({
      query: searchQuery || '',
      clients: algoliaClients,
      context: searchContext,
    }),
    getTopStripProps(apollo, country, centreSlug),
  ])

  return {
    props: {
      topStripProps,
      headerStaticData,
      algoliaResults: removeUndefinedProps({
        ...algoliaResults,
        products: undefined,
      }),
      country,
      centre,
      canonical,
    },
  }
}

const CentreSearchResultsPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = props => {
  const {
    country,
    topStripProps,
    centre,
    canonical,
    algoliaResults,
    headerStaticData,
  } = props
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
  })

  const metaData = {
    title: `${meta.title} - Search Results`,
    description: meta.description,
  }

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <>
      <SGLinkedData item={meta.schema} />
      <OpenGraph data={meta.openGraphData} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={{ ...centre, contactLinks }}
        header={{
          variant: 'centre',
          data: headerStaticData,
          logoText: centre.title,
        }}
        metaData={metaData}
        showRobotsMetaTag={false}
      >
        <SearchResultsPage centre={centre} algoliaResults={algoliaResults} />
      </Layout>
    </>
  )
}

export default CentreSearchResultsPage
